import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select, {components } from 'react-select';
import Icon from 'SF/MaterialIcon';
import css from "./StyledSelect.css";

const multilineOption = props => (
    <components.Option {...props}>
      { props.data.label.trim() }
      {props.data.description && <div className={css['description']}> { props.data.description } </div>}
    </components.Option>
);

const DropdownIndicator = () => (
  <Icon
    style={{marginRight: "16px", color: "#57595B"}}
    icon="arrow_drop_down"
    aria-hidden='true'
  />
);

const DisabledIndicator = () => (
  <Icon
    style={{marginRight: "20px", fontSize: "16px", color: "#616365"}}
    icon="lock"
    aria-hidden='true'
  />
);

const MenuIndicator = () => (
    <Icon
        style={{color: "#1E70A6"}}
        icon="more_vert"
        aria-hidden='true' />
);

const singleValueNoWhiteSpace = props => (
  <components.SingleValue {...props}>
    { props.data.label.trim() }
  </components.SingleValue>
);

const StyledSelect = React.forwardRef((props, ref) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isMultiline = !!props.options.find(option => option.description);
  return (
    <div role="application">
      <Select
        id={props.id}
        className={props.className}
        onChange={props.onChange}
        value={props.value}
        ref={ref}
        options={props.options}
        isLoading={props.isLoading}
        isDisabled={props.isDisabled}
        filterOption={props.filterOption}
        isSearchable={props.isSearchable}
        isClearable={props.isClearable}
        placeholder={props.placeholder}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        onKeyDown={event => {
          // If the dropdown is open and the user presses "ESC", we stop propagation to
          // avoid closing the whole Deep Link modal
          if ( event.keyCode === 27 && menuIsOpen ) event.stopPropagation();
        }}
        onInputChange={props.onSearchChange}
        noOptionsMessage={props.noOptionsMessage}
        components={{
          DropdownIndicator: props.hideDropdownIcon
            ? null
            : (props.isDisabled ? DisabledIndicator : (
                props.isMenu ? MenuIndicator : DropdownIndicator)),
          SingleValue: (props.singleValueNoWhiteSpace ? singleValueNoWhiteSpace : components.SingleValue),
          Option: props.customOption || (isMultiline ? multilineOption : components.Option)
        }}
        aria-label={props.ariaLabel || props['aria-label']}
        maxMenuHeight={props.maxMenuHeight}
        styles={{
          indicatorSeparator: provided => ({
            ...provided,
            width: 0,
          }),
          placeholder: (provided, state) => ({
            ...provided,
            color: state.isDisabled ? "#252628" : "hsl(0,0%,50%)",
            fontSize: "14px",
            overflow: "hidden",
            'white-space': "nowrap",
            'text-overflow': "ellipsis",
            width: "100%"
          }),
          control: (provided, state) => ({
            ...provided,
            height: "40px",
            border: 0,
            boxShadow: props.hasError
              ? "0 0 1px 2px #C70000"
              : state.isFocused
              ? "0 0 1px 3px rgba(0,93,255,0.5)"
              : "0 0 1px 2px #878C8C"
          }),
          singleValue: provided => ({
            ...provided,
            color: "#252628",
            fontSize: "14px"
          }),
          menu: provided => ({
            ...provided,
            marginTop: "4px",
            zIndex: "3",
            marginBottom: "30px"
          }),
          menuList: provided => ({
            ...provided,
            padding: 0,
          }),
          option: (provided, state) => ({
            ...provided,
            fontSize: "14px",
            verticalAlign: "middle",
            boxSizing: "border-box",
            color: state.isFocused ? "#154D72" : "#252628",
            paddingLeft: "30px",
            paddingTop: "12px",
            paddingBottom: "12px",
            border: state.isFocused ? "solid rgba(0,93,255,0.5)" : null,
            backgroundColor: state.isFocused ? "#D6EAF8" : (state.isSelected ? "#EFEFEF" : null),
            '::before': state.isSelected ? {
              position: "absolute",
              left: "8px",
              content: '"check"',
              fontFamily: 'Material Icons',
              fontSize: "14px",
            } : null
          })
        }}
      />
    </div>
  );
});

StyledSelect.defaultProps = {
  placeholder: "",
  hideDropdownIcon: false,
  isSearchable: false,
  isDisabled: false,
  isMenu: false,
  maxMenuHeight: 144,
  singleValueNoWhiteSpace: false
};

StyledSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isMenu: PropTypes.bool,
  hasError: PropTypes.bool,
  hideDropdownIcon: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  singleValueNoWhiteSpace: PropTypes.bool,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  customOption: PropTypes.func
};

export default StyledSelect;
